import type {
    InboxWorkflowItem,
    InboxWorkflowRowOutcomeMap,
    InboxWorkflowState,
    InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import { buildAppointmentTimeTagPresentation } from "../../../../../../../../../../../../../../../_shared/_logic/appointment-time-tags"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../../../../../../../../../../_shared/_logic/communication-presentation"
import {
  buildRichTooltip,
  tooltipField,
} from "../../../../../../../../../../../../../../../_shared/_logic/rich-tooltips"
import {
  PROPERTY_CUSTOMER_ROLES,
  type PropertyCustomerRole,
} from "../../../../../../../../../../../../../../../_shared/_logic/enums"
import { formatPropertyCustomerRole } from "../../../../../../../../../../../../../_shared/_logic/customer-roles"
import { formatInboxSourceLabel } from "../../../../../../../_shared/_logic/cells-presentation"
import { resolveCustomerStatement } from "../../../../../_shared/_logic/customer-statement"
import {
  mutationContactPointsForTag,
  mutationContactValue,
  normalizeWrongRecipientReplacementOutput,
} from "../../../../../_shared/_logic/replacement-output"
import type {
  WrongRecipientAppointmentPreview,
  WrongRecipientEditableField,
  WrongRecipientFieldMeta,
  WrongRecipientOriginSource,
  WrongRecipientPreviewPayload,
  WrongRecipientReplacementDraft,
  WrongRecipientReplacementRow,
  WrongRecipientRoleDraft,
} from "../../../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {}

const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const roleValue = (...values: unknown[]): WrongRecipientRoleDraft => {
  const role = stringValue(...values).toUpperCase()

  return PROPERTY_CUSTOMER_ROLES.includes(role as PropertyCustomerRole)
    ? (role as WrongRecipientRoleDraft)
    : ""
}

const stepOutput = (item: InboxWorkflowItem, stepKey: string): JsonObject =>
  objectValue(item.steps.find((entry) => entry.step_key === stepKey)?.output)

const relevantItem = (
  item: InboxWorkflowItem,
  workflow: InboxWorkflowState,
  step: InboxWorkflowStep,
) =>
  item.action_case.flow_key === workflow.flow.flow_key &&
  (item.steps.some((entry) => entry.step_key === step.step_key) ||
    step.requirements_resolved_by_step.some((requirement) =>
      item.action_case.requirements.includes(requirement),
    ))

const itemNeedsStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.some(
    (requirement) =>
      item.action_case.requirements.includes(requirement) &&
      !item.action_case.resolved_requirements.includes(requirement),
  )

const itemResolvedStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = item.steps.find((entry) => entry.step_key === step.step_key)

  if (projected?.status === "resolved") return true

  if (projected?.status === "skipped") return false

  const requirements = step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const fieldMeta = (
  initialValue: string,
  initialSource: WrongRecipientOriginSource,
): WrongRecipientFieldMeta => ({
  initialValue,
  initialSource: initialValue ? initialSource : "none",
  currentSource: initialValue ? initialSource : "none",
})

const appointmentPresentation = (appointment: WrongRecipientAppointmentPreview | null) => {
  if (!appointment) return null

  const time = buildAppointmentTimeTagPresentation({
    start_at: appointment.startAt,
    end_at: appointment.endAt,
    flex_type: appointment.flexType,
    timeframe_start_at: appointment.timeframeStartAt,
    timeframe_end_at: appointment.timeframeEndAt,
    customer_window_start_at: appointment.customerWindowStartAt,
    customer_window_end_at: appointment.customerWindowEndAt,
  })
  const communication = buildScheduleEventCommunicationTagPresentation({
    status: appointment.status,
    presentation: appointment.communicationPresentation,
  })
  const statusLabel = communication.label

  return {
    status: { ...communication.definition, label: time.label },
    subLabel: statusLabel,
    tooltipContent:
      communication.tooltip +
      buildRichTooltip([
        tooltipField(
          time.usesCustomerWindow ? "Kalendertermin" : "Zeit",
          time.scheduledPeriodLabel ?? time.label,
        ),
        tooltipField("Status", statusLabel),
        tooltipField("Mitarbeiter", appointment.employeeName),
      ]),
    fallbackLabel: time.label || "Kein Termin",
  }
}

const durableOutputError = "Gespeicherte Empfänger-Entscheidung fehlt oder ist ungültig."

export const buildWrongRecipientReplacementRows = ({
  workflow,
  step,
  rowOutcomes,
  preview,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes: InboxWorkflowRowOutcomeMap
  preview: WrongRecipientPreviewPayload | null
}): WrongRecipientReplacementRow[] =>
  workflow.items
    .filter((item) => relevantItem(item, workflow, step))
    .map((item) => {
      const actionCaseId = item.action_case.id
      const payload = objectValue(item.action_case.payload)
      const property = objectValue(payload.property)
      const propertyAddress = objectValue(property.address)
      const propertyUnit = objectValue(property.unit)
      const wrongRecipient = objectValue(payload.wrong_recipient)
      const payloadCustomer = objectValue(wrongRecipient.wrong_recipient_customer)
      const replacementCustomer = objectValue(wrongRecipient.replacement_customer)
      const replacementContact = objectValue(wrongRecipient.replacement_contact)
      const currentContactPoint = objectValue(wrongRecipient.current_contact_point)
      const sourcePresentation = objectValue(payload.presentation)
      const origin = objectValue(payload.origin)
      const output = stepOutput(item, "replace_wrong_recipient")
      const rowContext = preview?.rowContextByActionCaseId[actionCaseId]
      const previewReason =
        preview?.rowErrorsByActionCaseId[actionCaseId] ||
        (!preview ? "Empfänger-Vorschau fehlt." : !rowContext ? "Empfänger-Kontext fehlt." : "")
      const appointment = rowContext?.appointmentId
        ? (preview?.appointmentsById[rowContext.appointmentId] ?? null)
        : null
      const needsStep = itemNeedsStep(item, step)
      const resolved = itemResolvedStep(item, step)
      const skipped =
        item.steps.find((entry) => entry.step_key === step.step_key)?.status === "skipped"
      const outcome = rowOutcomes[actionCaseId] ?? null
      const persisted = normalizeWrongRecipientReplacementOutput(output)
      const useDurableOutput = resolved
      const affectedCustomer = useDurableOutput ? (persisted?.affectedCustomer ?? null) : null
      const replacementSnapshot = useDurableOutput ? (persisted?.replacementCustomer ?? null) : null
      const contact = useDurableOutput ? null : (rowContext?.recipientContact ?? null)
      const role = useDurableOutput
        ? roleValue(affectedCustomer?.role)
        : roleValue(
            wrongRecipient.role,
            payloadCustomer.role_hint,
            payload.property_customer_role,
            contact?.role,
          )
      const outputError = resolved && !persisted ? durableOutputError : null
      const activePreviewReason = needsStep ? previewReason : ""
      // Portal and bot reports name no role for the new contact; the replacement takes
      // over the wrong recipient's relation unless the operator picks another role.
      const suggestedRole = useDurableOutput
        ? roleValue(replacementSnapshot?.role)
        : roleValue(wrongRecipient.replacement_role) || role
      const suggestedName = useDurableOutput
        ? (replacementSnapshot?.displayName ?? "")
        : stringValue(replacementCustomer.label, replacementCustomer.name)
      const suggestedEmail = useDurableOutput
        ? mutationContactValue(replacementSnapshot, ["EMAIL"])
        : stringValue(replacementContact.email)
      const suggestedPhone = useDurableOutput
        ? mutationContactValue(replacementSnapshot, ["MOBILE", "FIXED_LINE"])
        : stringValue(replacementContact.phone)
      const customerLabel = useDurableOutput
        ? (affectedCustomer?.displayName ?? "Unbekannter Ansprechpartner")
        : stringValue(contact?.displayName, payloadCustomer.label, payloadCustomer.name) ||
          "Unbekannter Ansprechpartner"
      const submittedContactLabel = resolveCustomerStatement(wrongRecipient.wrong_recipient_hint)
      const addressLabel =
        stringValue(propertyAddress.label, propertyAddress.freeform, property.label) ||
        "Liegenschaft"
      const unitLabel = stringValue(propertyUnit.label, propertyUnit.hint) || "Einheit"
      const disabled =
        outcome?.status === "success" ||
        !needsStep ||
        !item.compatible ||
        Boolean(activePreviewReason) ||
        Boolean(outputError)

      return {
        id: actionCaseId,
        actionCaseId,
        workflowItem: item,
        step: item.steps.find((entry) => entry.step_key === step.step_key) ?? null,
        payload: item.action_case.payload,
        caseTitle: item.action_case.title,
        customerLabel,
        customerEmail: useDurableOutput
          ? mutationContactValue(affectedCustomer, ["EMAIL"])
          : contact?.email ||
            (stringValue(currentContactPoint.kind).toUpperCase() === "EMAIL"
              ? stringValue(currentContactPoint.value)
              : ""),
        customerPhone: useDurableOutput
          ? mutationContactValue(affectedCustomer, ["MOBILE", "FIXED_LINE"])
          : contact?.phone ||
            (["MOBILE", "FIXED_LINE"].includes(stringValue(currentContactPoint.kind).toUpperCase())
              ? stringValue(currentContactPoint.value)
              : ""),
        customerContactPoints: useDurableOutput
          ? mutationContactPointsForTag(affectedCustomer)
          : undefined,
        addressLabel,
        unitLabel,
        appointmentPresentation: appointmentPresentation(appointment),
        roleLabel: role ? formatPropertyCustomerRole(role) : "Ansprechpartner",
        sourceLabel: formatInboxSourceLabel(
          stringValue(sourcePresentation.source_label, origin.source, item.action_case.source),
        ),
        submittedContactLabel,
        suggestedName,
        suggestedEmail,
        suggestedPhone,
        suggestedRole,
        persistedAction: persisted?.action ?? null,
        needsStep,
        rowOutcome: outcome,
        isOpen: needsStep,
        isResolved: resolved,
        isCompatible: item.compatible,
        disabled,
        statusLabel:
          outcome?.status === "failed"
            ? "Prüfung nötig"
            : outputError
              ? "Datenfehler"
              : skipped
                ? "Übersprungen"
                : resolved
                  ? "Erledigt"
                  : activePreviewReason || !item.compatible
                    ? "Nicht verfügbar"
                    : "Offen",
        statusDetail:
          outcome?.error ||
          outcome?.detail ||
          outputError ||
          activePreviewReason ||
          item.error ||
          null,
        statusHint: outputError || activePreviewReason || null,
        searchText: [
          item.action_case.title,
          customerLabel,
          contact?.email,
          contact?.phone,
          addressLabel,
          unitLabel,
          submittedContactLabel,
          suggestedName,
          suggestedEmail,
          suggestedPhone,
        ]
          .filter(Boolean)
          .join(" "),
      }
    })

export const defaultWrongRecipientReplacementDraft = (
  row: WrongRecipientReplacementRow,
): WrongRecipientReplacementDraft => {
  const action =
    row.persistedAction ??
    (!row.needsStep
      ? ""
      : row.suggestedName || row.suggestedEmail || row.suggestedPhone
        ? "replace"
        : "remove")

  return {
    initialAction: action,
    action,
    displayName: row.suggestedName,
    email: row.suggestedEmail,
    phone: row.suggestedPhone,
    role: row.suggestedRole,
    fieldMeta: {
      displayName: fieldMeta(row.suggestedName, "customer"),
      email: fieldMeta(row.suggestedEmail, "customer"),
      phone: fieldMeta(row.suggestedPhone, "customer"),
      role: fieldMeta(row.suggestedRole, "customer"),
    },
  }
}

export const updateWrongRecipientDraftField = (
  draft: WrongRecipientReplacementDraft,
  field: WrongRecipientEditableField,
  value: string,
  source: WrongRecipientOriginSource,
): WrongRecipientReplacementDraft => ({
  ...draft,
  [field]: field === "role" ? roleValue(value) : value,
  fieldMeta: {
    ...draft.fieldMeta,
    [field]: {
      ...draft.fieldMeta[field],
      currentSource: value ? source : "none",
    },
  },
})
